/**
 * DataBank — central artifact repository (Sprint P37y · Sprint F highlight)
 * Aggregates every generated artefact for the project:
 *   • Original uploaded files (collar/assay/lithology/etc.)
 *   • GIS map gallery + boundary versions
 *   • Exports (map book PDF, log book PDF, CPR PDF)
 *   • Configuration versions
 *   • Report drafts
 *
 * All data comes from existing endpoints — the Data Bank is a UI-level
 * aggregator, no new backend collection is created.
 */
import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Archive, DownloadSimple, ClockCounterClockwise, Files,
  Sparkle, MapTrifold, ChartLineUp, FilePdf, Gear, ArrowSquareOut,
  PushPin, PushPinSlash,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import FlowNav from "@/components/FlowNav";
import ArtifactPreviewPopover from "./ArtifactPreviewPopover";
import PinnedPreviewPanel from "./PinnedPreviewPanel";

export default function DataBank() {
  const { pid } = useParams();
  const nav = useNavigate();
  // Iter88 · Feb 2026 · Pinned side-by-side compare (max 2). Each entry
  // is `{ id, preview }` so the panel can render via ArtifactPreviewCard.
  const [pinned, setPinned] = useState([]);
  const togglePin = (id, preview) => {
    setPinned((cur) => {
      if (cur.find((p) => p.id === id)) {
        toast.info("Unpinned");
        return cur.filter((p) => p.id !== id);
      }
      if (cur.length >= 2) {
        toast.warning("Compare panel is full — unpin one first");
        return cur;
      }
      toast.success(`Pinned · ${cur.length + 1} of 2`);
      return [...cur, { id, preview }];
    });
  };
  const unpin = (id) => setPinned((cur) => cur.filter((p) => p.id !== id));
  const closePanel = () => setPinned([]);

  const configQ = useQuery({
    queryKey: ["dbank-config", pid],
    queryFn: () => api.get(`/projects/${pid}/configuration`).then((r) => r.data),
  });
  const historyQ = useQuery({
    queryKey: ["dbank-history", pid],
    queryFn: () => api.get(`/projects/${pid}/configuration/history?limit=50`).then((r) => r.data),
  });
  const galleryQ = useQuery({
    queryKey: ["dbank-gallery", pid],
    queryFn: () => api.get(`/projects/${pid}/gis/gallery`).then((r) => r.data),
  });
  const boundaryVersionsQ = useQuery({
    queryKey: ["dbank-boundaries", pid],
    queryFn: () => api.get(`/projects/${pid}/gis/boundary/versions`).then((r) => r.data),
  });
  const mapLatestQ = useQuery({
    queryKey: ["dbank-map-latest", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=gis_map_book`).then((r) => r.data).catch(() => null),
  });
  const logLatestQ = useQuery({
    queryKey: ["dbank-log-latest", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=borehole_log_book`).then((r) => r.data).catch(() => null),
  });
  const cprLatestQ = useQuery({
    queryKey: ["dbank-cpr-latest", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=cpr`).then((r) => r.data).catch(() => null),
  });

  const bundle = useMemo(() => {
    const sections = [];
    sections.push({
      key: "config", title: "Configuration",
      Icon: Gear, cta: `/app/projects/${pid}/setup/configuration`,
      items: [{
        label: "Current project configuration",
        meta: `v${configQ.data?.configuration?.version_number || 1} · ${(configQ.data?.configuration?.updated_at || "").slice(0, 16).replace("T", " ") || "—"}`,
        primary: (configQ.data?.configuration?.updated_by || "system"),
        actionLabel: "Open",
        to: `/app/projects/${pid}/setup/configuration`,
        preview: {
          kind: "metadata",
          title: `Current project configuration · v${configQ.data?.configuration?.version_number || 1}`,
          filename: "configuration.json",
          timestamp: configQ.data?.configuration?.updated_at,
          description: "Latest snapshot of every project setting (client · CP · standards · commodity · CRS · methodology). Open to edit.",
        },
      }],
    });
    // Config history
    const hist = (historyQ.data?.entries || []).slice(0, 20);
    if (hist.length) {
      sections.push({
        key: "config_history", title: "Configuration Versions",
        Icon: ClockCounterClockwise, cta: `/app/projects/${pid}/setup/configuration`,
        items: hist.map((h) => ({
          label: `v${h.version_number} · ${h.change_summary || "(no summary)"}`,
          meta: `${new Date(h.at).toLocaleString()} · ${(h.changed_fields || []).length} fields`,
          primary: h.user_id || "system",
          actionLabel: h.kind === "ai_suggestions" ? "AI" : "Diff",
          to: `/app/projects/${pid}/setup/configuration`,
        })),
      });
    }
    // Boundary versions
    const bv = boundaryVersionsQ.data?.versions || [];
    if (bv.length) {
      sections.push({
        key: "boundaries", title: "Boundary Versions",
        Icon: MapTrifold, cta: `/app/projects/${pid}/gis`,
        items: bv.map((v) => ({
          label: `v${v.version} · ${v.filename || "boundary"}`,
          meta: `${v.area_km2 ? Number(v.area_km2).toFixed(2) + " km² · " : ""}${v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : "?"}`,
          primary: v.approved ? "APPROVED" : v.restored_from ? `restored v${v.restored_from}` : "draft",
          actionLabel: "View",
          to: `/app/projects/${pid}/gis`,
          preview: {
            kind: "boundary",
            title: `${v.filename || "Boundary"} · v${v.version}`,
            geojson: v.geojson,
            bbox: v.bbox,
            area_km2: v.area_km2,
            perimeter_km: v.perimeter_km,
            source_crs: v.source_crs,
            utm_epsg: v.utm_epsg,
            centroid: v.centroid,
          },
        })),
      });
    }
    // GIS gallery
    const gallery = galleryQ.data?.maps || [];
    if (gallery.length) {
      sections.push({
        key: "gis_gallery", title: `GIS Maps (${gallery.length})`,
        Icon: MapTrifold, cta: `/app/projects/${pid}/gis`,
        items: gallery.slice(0, 12).map((m) => ({
          label: m.title,
          meta: `${m.category} · ${Math.round(m.size_bytes / 1024)} KB`,
          primary: (m.sha256 || "").slice(0, 8),
          actionLabel: "PNG",
          to: `/app/projects/${pid}/gis`,
          preview: {
            kind: "image",
            title: `${m.title} · ${m.category}`,
            image_url: m.preview_url || m.download_url,
            size_kb: Math.round((m.size_bytes || 0) / 1024),
            sha256: m.sha256,
            timestamp: m.generated_at,
            filename: m.filename,
          },
        })),
      });
    }
    // Exports
    const exports = [];
    if (mapLatestQ.data) exports.push({ ...mapLatestQ.data, kind: "GIS Map Book" });
    if (logLatestQ.data) exports.push({ ...logLatestQ.data, kind: "Log Book" });
    if (cprLatestQ.data) exports.push({ ...cprLatestQ.data, kind: "CPR" });
    if (exports.length) {
      sections.push({
        key: "exports", title: "Latest Exports",
        Icon: FilePdf, cta: `/app/projects/${pid}/gis`,
        items: exports.map((e) => ({
          label: `${e.kind} · ${e.filename}`,
          meta: `${Math.round((e.file_size_bytes || 0) / 1024)} KB · ${e.created_at ? new Date(e.created_at).toLocaleString() : "?"}`,
          primary: e.report_id ? e.report_id.slice(0, 8) : "",
          actionLabel: "Download",
          to: e.download_url || `/app/projects/${pid}/gis`,
          download: e.id,
          preview: {
            kind: "metadata",
            title: `${e.kind} · ${e.filename}`,
            filename: e.filename,
            size_kb: Math.round((e.file_size_bytes || 0) / 1024),
            sha256: e.sha256,
            timestamp: e.created_at,
            description: `Latest ${e.kind} export ready to download as a PDF.`,
          },
        })),
      });
    }
    return sections;
  }, [pid, configQ.data, historyQ.data, galleryQ.data, boundaryVersionsQ.data,
       mapLatestQ.data, logLatestQ.data, cprLatestQ.data]);

  const totalItems = bundle.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50" data-testid="data-bank">
      <header className="shrink-0 bg-white border-b border-slate-200 z-10">
        <div className="px-6 py-3 flex items-center gap-4">
          <button onClick={() => nav(`/app/projects/${pid}/setup`)}
                     className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 hover:text-brand-blue"
                     data-testid="btn-back-to-setup">
            <ArrowLeft size={12} /> Project Details
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">Stage 9 of 9 · Download · preview · version history</div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none truncate">
              Reports &amp; Downloads
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {totalItems} artefact{totalItems === 1 ? "" : "s"} across {bundle.length} categor{bundle.length === 1 ? "y" : "ies"} · all generated reports are stored here
            </div>
          </div>
          {/* Iter88 · Feb 2026 · Compare-panel toggle in header when
              artefacts are pinned. */}
          {pinned.length > 0 && (
            <button
              type="button"
              onClick={closePanel}
              data-testid="pinned-preview-header-toggle"
              className="inline-flex items-center gap-1 text-[11px] text-brand-blue
                          border border-brand-blue/40 rounded-md px-2 py-1 hover:bg-brand-blue/5"
            >
              <PushPinSlash size={11} /> Clear compare ({pinned.length})
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 min-h-0 flex overflow-hidden">
        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4"
                data-testid="data-bank-main">
        <div className="max-w-7xl mx-auto space-y-4 pb-8">
          {bundle.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <Archive size={22} className="text-slate-400 mx-auto mb-2" weight="duotone" />
              <div className="text-[13px] text-slate-700 font-semibold">Data Bank is empty</div>
              <div className="text-[11.5px] text-slate-500 mt-1">Upload data, generate maps, or produce a CPR to populate the bank.</div>
            </div>
          )}
          {bundle.map((s) => (
            <section key={s.key} className="rounded-lg bg-white border border-slate-200" data-testid={`bank-section-${s.key}`}>
              <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                <s.Icon size={14} className="text-brand-blue" weight="duotone" />
                <h2 className="font-heading font-bold text-[13.5px] text-slate-900">{s.title}</h2>
                <span className="ml-auto text-[10px] text-slate-400 font-mono">{s.items.length} item{s.items.length === 1 ? "" : "s"}</span>
                <Link to={s.cta}
                         className="inline-flex items-center gap-0.5 text-[10.5px] text-brand-blue hover:underline"
                         data-testid={`bank-cta-${s.key}`}>
                  Open <ArrowSquareOut size={9} />
                </Link>
              </header>
              <ul className="divide-y divide-slate-100">
                {s.items.map((it, i) => {
                  const rowId = `${s.key}-${i}`;
                  const isPinned = !!pinned.find((p) => p.id === rowId);
                  return (
                  <ArtifactPreviewPopover key={i} preview={it.preview}>
                    <li className="px-4 py-2 flex items-center gap-3 text-[11.5px] cursor-default hover:bg-slate-50/60 transition-colors"
                          data-testid={`bank-item-${s.key}-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 truncate">{it.label}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">{it.meta}</div>
                      </div>
                      <div className="text-[10.5px] text-slate-400 font-mono truncate w-40 text-right">{it.primary}</div>
                      {it.preview && (
                        <button
                          type="button"
                          onClick={() => togglePin(rowId, it.preview)}
                          data-testid={`bank-pin-${s.key}-${i}`}
                          title={isPinned ? "Unpin from compare" : "Pin to compare panel"}
                          className={`inline-flex items-center gap-0.5 rounded-md p-1 transition ${
                            isPinned
                              ? "text-brand-blue bg-brand-blue/10"
                              : "text-slate-400 hover:text-brand-blue hover:bg-brand-blue/5"
                          }`}
                        >
                          {isPinned ? <PushPinSlash size={11} /> : <PushPin size={11} />}
                        </button>
                      )}
                      {it.download ? (
                        <a href="#" onClick={async (e) => {
                              e.preventDefault();
                              const r = await api.get(`/projects/${pid}/exports/${it.download}/download`, { responseType: "blob" });
                              const url = URL.createObjectURL(r.data);
                              const a = document.createElement("a");
                              a.href = url; a.download = "artifact.pdf";
                              document.body.appendChild(a); a.click(); a.remove();
                              setTimeout(() => URL.revokeObjectURL(url), 5000);
                            }} data-testid={`bank-download-${s.key}-${i}`}
                            className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-brand-blue hover:bg-brand-blue/5">
                          <DownloadSimple size={10} /> {it.actionLabel}
                        </a>
                      ) : (
                        <Link to={it.to} className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-brand-blue hover:bg-brand-blue/5"
                                  data-testid={`bank-link-${s.key}-${i}`}>
                          {it.actionLabel} →
                        </Link>
                      )}
                    </li>
                  </ArtifactPreviewPopover>
                );})}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <PinnedPreviewPanel pinned={pinned} onUnpin={unpin} onClose={closePanel} />
      </div>
      {/* Iter87 · Feb 2026 · Stage 9 · unified footer (Dashboard · Back
          · Save · Save & Continue). Save & Continue on the terminal
          stage returns the user to the Projects list. */}
      <FlowNav
        pid={pid}
        hideCancel
        hideNext
        onSave={() => toast.success("Reports panel refreshed")}
        onSaveContinue={() => nav(`/app/projects`)}
        backLabel="Back · Generate Report"
      />
    </div>
  );
}
